// =============================================================================
// QVPN Site Configuration - Single Source of Truth
// =============================================================================
// This file contains all centralized configuration for the QVPN marketing website.
// Update values here to reflect changes across the entire site.
// =============================================================================

// =============================================================================
// BRAND INFORMATION
// =============================================================================
export const SITE_CONFIG = {
  brand: {
    name: "QVPN",
    tagline: "Quantum Safe VPN",
    description: "Quantum safe VPN. Protect your data against today's hackers and tomorrow's quantum computers.",
    domain: "qvpn.io",
    url: "https://qvpn.io"
  },

  // Announcement banner shown at top of site
  announcement: {
    enabled: true,
    text: "Now live in 30+ countries. Start protecting your data today.",
    ctaLabel: "See Plans",
    ctaHref: "/pricing"
  },

  // ===========================================================================
  // LEGAL BUSINESS INFORMATION
  // ===========================================================================
  // TODO: Update businessAddress before public launch
  // TODO: Update governingLaw with specific jurisdiction if needed
  // ===========================================================================
  legal: {
    operatorName: "DIAMANTE FINANCIAL TECHNOLOGIES L.L.C.",
    operatorDisclosure: "QVPN is operated by DIAMANTE FINANCIAL TECHNOLOGIES L.L.C., Abu Dhabi, United Arab Emirates.",
    copyrightYear: new Date().getFullYear(),
    
    businessAddress: "Office 202, Premise No: 1909081141, P-2264, E10, Al Danah, Abu Dhabi, United Arab Emirates",
    
    // Governing law statement
    governingLaw: "These Terms are governed by the laws of the United Arab Emirates and the Emirate of Abu Dhabi, unless otherwise required by applicable law."
  },

  // ===========================================================================
  // CONTACT INFORMATION
  // ===========================================================================
  contact: {
    supportEmail: "support@qvpn.io",      // Primary public support email
    businessEmail: "hello@qvpn.io",        // Business inquiries
    privacyEmail: "privacy@qvpn.io",       // Privacy-related requests
    supportHours: "Monday - Friday, 9:00 AM - 6:00 PM (EST)"
  },

  // ===========================================================================
  // SOCIAL LINKS
  // ===========================================================================
  // TODO: Update with actual social media URLs when accounts are created
  // ===========================================================================
  social: {
    twitter: "#",
    discord: "#",
    instagram: "#",
    linkedin: "#"
  },

  // ===========================================================================
  // APP STORE LINKS
  // ===========================================================================
  // TODO: Update with actual app store URLs when apps are published
  // ===========================================================================
  appLinks: {
    googlePlay: {
      url: "https://play.google.com/store/apps/details?id=io.qvpn.androidapp",
      available: false,
      label: "Android app — coming soon"
    },
    appStore: {
      url: "https://apps.apple.com/us/app/qvpn-secure-fast-vpn/id6761363074",
      available: false,
      label: "iOS app — coming soon"
    },
    platforms: ["Android", "iOS", "Windows", "macOS"]
  },

  // ===========================================================================
  // SEO DEFAULTS
  // ===========================================================================
  seo: {
    defaultTitle: "QVPN - Quantum Safe VPN | Post Quantum Encryption",
    titleTemplate: "%s | QVPN",
    defaultDescription: "QVPN is a quantum safe VPN built on post quantum cryptography. Protect your data against today's hackers and tomorrow's quantum computer attacks.",
    keywords: "quantum safe VPN, post quantum cryptography, quantum encryption, privacy VPN, secure browsing, public WiFi security",
    ogImage: "/og-image.png"
  }
};

// =============================================================================
// PRICING CONFIGURATION
// =============================================================================
// All pricing values are centralized here for easy updates.
// =============================================================================
export const PRICING_CONFIG = {
  currency: "USD",
  currencySymbol: "$",
  
  plans: {
    free: {
      id: "free",
      name: "Free",
      description: "Limited access with ads",
      price: {
        monthly: 0,
        yearly: 0
      },
      features: [
        "Ad-supported VPN access",
        "Access to limited server locations",
        "Standard connection speeds",
        "1 device connection",
        "Basic support"
      ],
      limitations: [
        "Limited daily usage",
        "Ads shown during connection"
      ],
      cta: "Get Started Free",
      highlighted: false
    },
    premium: {
      id: "premium",
      name: "Premium",
      description: "Unlimited VPN access with all features",
      price: {
        monthly: 9.99,
        yearly: 59.99
      },
      yearlyMonthlyEquivalent: 4.99, // $59.99 / 12
      yearlyBadge: "Best Value",
      features: [
        "Unlimited VPN access",
        "All server locations",
        "High-speed connections",
        "Up to 5 device connections",
        "Priority support",
        "Kill switch protection",
        "Auto-connect on untrusted networks",
        "No ads"
      ],
      cta: "Go Premium",
      highlighted: true,
      badge: "Most Popular"
    }
  },

  // ===========================================================================
  // QUANTUM PRICING TIERS (Homepage 3-tier layout)
  // ===========================================================================

  quantumPlans: [
    {
      id: "monthly",
      name: "Monthly",
      duration: "1 Month",
      price: 9.99,
      per: "Per Month",
      billing: "Billed monthly",
      features: [
        "Quantum safe encryption",
        "All server locations",
        "24/7 support"
      ],
      cta: "Get Started",
      badge: null,
      savings: null,
      highlighted: false
    },
    {
      id: "yearly",
      name: "Yearly",
      duration: "12 Months",
      price: 4.99,
      per: "Per Month",
      billing: "$59.88 billed annually",
      features: [
        "Everything in Monthly",
        "Priority support",
        "Early access to new features"
      ],
      cta: "Get Started",
      badge: "Popular",
      savings: "Save 50%",
      highlighted: true
    },
    {
      id: "twoYear",
      name: "2 Years",
      duration: "24 Months",
      price: 2.99,
      per: "Per Month",
      billing: "$71.76 billed every 2 years",
      features: [
        "Everything in Yearly",
        "Lowest price guaranteed"
      ],
      cta: "Get Started",
      badge: "Best Value",
      savings: "Save 70%",
      highlighted: false
    }
  ],

    // Free tier callout (homepage)
  freeTier: {
    name: "Try QVPN Free",
    body: "Get 90 minutes of quantum protection daily. No credit card required.",
    features: [
      "Quantum safe encryption",
      "Select server locations",
      "Full privacy protection"
    ],
    cta: "Start Free",
    subtext: "Want unlimited protection? Upgrade to Quantum Pro anytime."
  },

  // ===========================================================================
  // BILLING INFORMATION
  // ===========================================================================
  billing: {
    provider: "Stripe",
    securityNote: "Payments are processed securely via Stripe.",
    statementDescriptor: "QVPN*SUBSCRIPTION",
    autoRenewal: true,
    
    // Renewal copy
    renewalNote: "Subscriptions renew automatically unless canceled before the renewal date.",
    
    // Cancellation copy
    cancellationNote: "You can cancel anytime. Access continues until the end of your current billing period.",
    
    //Grace period
    gracePeriodDays: "7",

    // Grace period copy
    gracePeriodNote: "If payment fails, access may continue for a short grace period before downgrade to free tier."
  },

  // ===========================================================================
  // REFUND POLICY
  // ===========================================================================
  refund: {
    note: "Refunds are handled according to the Refund & Cancellation Policy and applicable law.",
    contactNote: "For refund requests, contact support@qvpn.io with your account details.",
    windowDays: "7",
  }
};

// =============================================================================
// PRIVACY & DATA COLLECTION INFORMATION
// =============================================================================
export const PRIVACY_CONFIG = {
  // What we collect
  dataCollected: {
    account: [
      "Email address (for account creation and communication)",
      "Password (securely hashed, never stored in plain text)"
    ],
    billing: [
      "Payment method details (processed by Stripe, not stored by us)",
      "Subscription status and billing history",
      "Transaction records for your account"
    ],
    device: [
      "Device type and operating system (for app compatibility)",
      "App version (for support and updates)"
    ],
    operational: [
      "Connection timestamps (for session management)",
      "Bandwidth usage (aggregate, for service optimization)",
      "Server selection preferences"
    ],
    support: [
      "Communications with our support team",
      "Feedback and feature requests you submit"
    ]
  },

  // What we do NOT collect
  dataNotCollected: [
    "Your browsing history or websites visited",
    "DNS queries while connected to VPN",
    "Your original IP address logs",
    "Traffic content or data packets"
  ],

  // Privacy statements
  statements: {
    noActivityLogging: "We do not log or monitor your browsing activity while connected to QVPN.",
    noAdTargeting: "We do not use your browsing activity for advertising or sell your data to third parties.",
    minimalCollection: "We collect only the minimum information necessary to operate the service, manage your account, process payments, and provide customer support.",
    dataRetention: "Account data is retained while your account is active. You may request deletion of your account and associated data at any time."
  }
};

// =============================================================================
// EMAIL COMMUNICATIONS (for SES review)
// =============================================================================
export const EMAIL_CONFIG = {
  transactionalEmails: [
    {
      type: "Account Verification",
      description: "Email to verify your email address when creating an account",
      trigger: "Account registration"
    },
    {
      type: "Password Reset",
      description: "Email with a secure link to reset your password",
      trigger: "Password reset request"
    },
    {
      type: "Account Deletion Confirmation",
      description: "Confirmation that your account deletion request has been received",
      trigger: "Account deletion request"
    },
    {
      type: "Account Recovery",
      description: "Email to recover a recently deleted account within the recovery window",
      trigger: "Account recovery request"
    },
    {
      type: "Payment Confirmation",
      description: "Receipt and confirmation of successful payment",
      trigger: "Successful subscription payment"
    },
    {
      type: "Subscription Renewal Reminder",
      description: "Reminder before your subscription automatically renews",
      trigger: "Before renewal date"
    },
    {
      type: "Payment Failed",
      description: "Notification that a payment attempt failed with instructions to update payment method",
      trigger: "Failed payment attempt"
    },
    {
      type: "Grace Period Warning",
      description: "Warning that your grace period is ending and Premium access may be lost",
      trigger: "During grace period after failed payment"
    },
    {
      type: "Subscription Cancelled",
      description: "Confirmation of subscription cancellation and when access ends",
      trigger: "Subscription cancellation"
    }
  ]
};

// =============================================================================
// ACCOUNT MANAGEMENT INFORMATION
// =============================================================================
export const ACCOUNT_CONFIG = {
  deletion: {
    recoveryPeriodDays: 14,
    note: "When you delete your account, it enters a 14-day recovery period. During this time, you can recover your account by logging in. After 14 days, your account and associated data are permanently deleted.",
    forceDeleteNote: "You may request immediate permanent deletion by contacting support@qvpn.io"
  },
  recovery: {
    note: "If you've recently deleted your account and want to recover it, simply try logging in within 14 days of deletion. You'll receive an option to restore your account."
  },
  subscription: {
    expiryBehavior: "When your Premium subscription ends or expires, your account automatically reverts to the Free plan with limited features.",
    gracePeriod: "If payment fails, access may continue for a short grace period before downgrade to free tier."
  }
};

// =============================================================================
// NAVIGATION LINKS
// =============================================================================
export const NAV_LINKS = {
  main: [
    { name: "Pricing", href: "/pricing" },
    { name: "Features", href: "/#features" },
    { name: "Download", href: "/#download" },
    // { name: "Login", href: "/login", external: false }
  ],
  footer: {
    product: [
      { name: "Features", href: "/#features" },
      { name: "Pricing", href: "/pricing" },
      { name: "Download", href: "/#download" },
      { name: "FAQ", href: "/faq" }
    ],
    company: [
      { name: "About", href: "/about" },
      { name: "Contact", href: "/contact" }
    ],
    support: [
      { name: "Help Center", href: "/help" },
      // { name: "Contact Support", href: "/contact" },
      { name: "Account & Billing", href: "/help/account-billing" }
    ],
    legal: [
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Terms of Service", href: "/terms" },
      { name: "Refund Policy", href: "/refund-policy" },
      { name: "Cookie Preferences", href: "/cookies" },
      { name: "Accessibility Statement", href: "/accessibility" }
    ],
    socials: [
      { name: "X (Twitter)", href: "https://x.com/QVPN_x" },
      { name: "Telegram", href: "https://t.me/QVPN_Announcements" }
    ]
  }
};

// =============================================================================
// FAQ DATA
// =============================================================================
export const FAQ_DATA = [
  {
    category: "General",
    questions: [
      {
        q: "What is QVPN?",
        a: "QVPN is a quantum safe VPN (Virtual Private Network) service built on post quantum cryptography. It encrypts your internet connection to protect your data against both today's cyber threats and tomorrow's quantum computer attacks."
      },
      {
        q: "Which devices does QVPN support?",
        a: "QVPN is available for Android and iOS. You can download the app from the Google Play Store or Apple App Store."
      },
      {
        q: "How does QVPN protect my privacy?",
        a: "QVPN encrypts all data transmitted between your device and our servers using post quantum cryptography — encryption algorithms specifically designed to resist quantum computer attacks. This keeps your data private today and future-proof against quantum decryption."
      },
      {
        q: "What is post quantum cryptography?",
        a: "Post quantum cryptography refers to encryption algorithms designed to remain secure even against attacks from powerful quantum computers. Unlike traditional encryption, these algorithms are based on mathematical problems that remain hard to solve even with quantum computing."
      },
      {
        q: "Why do I need a quantum safe VPN?",
        a: "Hackers and state actors are already collecting encrypted data today (a strategy called 'Harvest Now, Decrypt Later') — planning to decrypt it when quantum computers become powerful enough. A quantum safe VPN protects your data against this future threat."
      }
    ]
  },
  {
    category: "Privacy & Data",
    questions: [
      {
        q: "Does QVPN log my browsing activity?",
        a: "No. We do not log, monitor, or store your browsing activity, websites visited, or DNS queries while connected to QVPN."
      },
      {
        q: "What information does QVPN collect?",
        a: "We collect only the minimum information necessary to operate the service: your email address for account management, payment information processed securely by Stripe, basic device information for app compatibility, and connection timestamps for session management. We do not collect or store your browsing history."
      },
      {
        q: "Is my data sold to third parties?",
        a: "No. We do not sell, rent, or share your personal data with third parties for advertising or marketing purposes."
      }
    ]
  },
  {
    category: "Subscriptions & Billing",
    questions: [
      {
        q: "How do subscriptions work?",
        a: "Premium subscriptions are billed monthly or yearly, depending on your chosen plan. Subscriptions renew automatically unless canceled before the renewal date."
      },
      {
        q: "How do I cancel my subscription?",
        a: "You can cancel your subscription at any time through your account settings in the app or by contacting support@qvpn.io. When you cancel, you retain Premium access until the end of your current billing period."
      },
      {
        q: "What happens if my payment fails?",
        a: "If payment fails, access may continue for a short grace period before downgrade to free tier. You'll receive email notifications to update your payment method."
      },
      {
        q: "What happens when my subscription expires?",
        a: "When your Premium subscription ends or expires, your account automatically reverts to the Free plan. You can continue using QVPN with the Free plan's limitations, or resubscribe to Premium at any time."
      },
      {
        q: "Can I get a refund?",
        a: "Refunds are handled according to the Refund & Cancellation Policy and applicable law. Please contact support@qvpn.io with your refund request."
      }
    ]
  },
  {
    category: "Account Management",
    questions: [
      {
        q: "How do I reset my password?",
        a: "Tap 'Forgot Password' on the login screen or go to your account settings. Enter your email address and we'll send you a secure link to reset your password."
      },
      {
        q: "How do I delete my account?",
        a: "You can delete your account through the app settings or by contacting support@qvpn.io. Deleted accounts enter a 14-day recovery period before permanent deletion."
      },
      {
        q: "Can I recover a deleted account?",
        a: "Yes, within 14 days of deletion. Simply try logging in with your credentials and you'll be given the option to restore your account. After 14 days, accounts are permanently deleted and cannot be recovered."
      },
      {
        q: "How do I contact support?",
        a: "Email us at support@qvpn.io. We typically respond within 24-48 hours during business days."
      }
    ]
  }
];

// =============================================================================
// TODO LIST FOR LAUNCH
// =============================================================================
// Before public launch, ensure the following are updated:
// 
// 1. SITE_CONFIG.legal.businessAddress
//    - Replace placeholder with actual business address
//
// 2. SITE_CONFIG.appLinks.googlePlay.url
//    - Update with actual Google Play Store URL
//    - Set available: true
//
// 3. SITE_CONFIG.appLinks.appStore.url  
//    - Update with actual Apple App Store URL
//    - Set available: true
//
// 4. SITE_CONFIG.social.*
//    - Update with actual social media URLs
//
// 5. Contact Form Integration
//    - Implement backend API endpoint for form submission
//    - Update ContactPage.jsx to use API instead of mailto fallback
//
// =============================================================================
